import type { Context, Kolom, Lijst } from '@xxtract/tabelfilters';
import { STAP_VALIDEREN, STAP_VASTLEGGEN } from './kerncijfers';
import { naamVoorRegel } from './naam';
import type { StapFilter, StapKeuze } from './stapFilter';
import type { HandlingTimePerPersonRow } from './handlingTime.api';

/**
 * Story 31.4 — het rekenwerk onder de tabel per persoon.
 *
 * Hier staat uitsluitend rekenwerk, om dezelfde reden als bij de kerncijfers: de scherpste regels
 * van deze story zijn rekenregels en geen opmaak, en ze horen los van een scherm toetsbaar te zijn.
 *
 * De optelling gebeurt HIER en niet bij de bron, precies zoals de kaarten erboven dat doen. Zo
 * kunnen de kolommen en de kaarten niet uit elkaar lopen zonder dat iemand het merkt — dat is wat
 * AC-6 vastlegt.
 */

/** De uitkomst die één regel voor een ONBEKEND aantal mensen samenvouwt (epic 31 §2). */
export const UITKOMST_PERSOON_VERWIJDERD = 'person-removed';
/** De dienst zelf, nummer 0. Geen collega, en volgens epic 31 §2 een teken dat er iets stuk is. */
export const UITKOMST_GEAUTOMATISEERD = 'automated-system';

export interface StapRegel {
  /** Mensminuten in deze stap. `null` — nooit `0` — zodra er niets gemeten is. */
  minuten: number | null;
  /** Het aantal behandelingen in deze stap. Nadrukkelijk geen ontdubbelde stuks. */
  behandelingen: number | null;
  /** Minuten van deze stap gedeeld door de behandelingen van DÍE stap. */
  gemiddeldePerStuk: number | null;
}

export interface TabelRegel {
  userId: number;
  /** Alleen aanwezig bij `named`. Nooit invullen met een verzonnen waarde. */
  userName?: string;
  userNameOutcome: string;
  vastleggen: StapRegel;
  valideren: StapRegel;
  /** De som van de twee stappen; `null` zodra er in geen van beide iets gemeten is. */
  totaalMinuten: number | null;
  /**
   * De som van de behandelingen over de twee stappen.
   *
   * Uitdrukkelijk GEEN aantal stuks: de bron telt stuks ontdubbeld, en een stuk dat door beide
   * stappen ging telt hier dus twee keer (epic 31 §4).
   */
  behandelingen: number | null;
  /**
   * Blijft buiten elke sortering en staat altijd onderaan.
   *
   * Waar: bij `person-removed`. Die regel vouwt een onbekend aantal mensen samen, en tussen de
   * mensen in gesorteerd leest hij als één collega die heel veel of heel weinig deed.
   */
  buitenSortering: boolean;
  /**
   * Of deze regel onderaan de tabel hoort te blijven, buiten de gesorteerde groep.
   *
   * DRIE BEGRIPPEN EN GEEN TWEE, en dat is een reviewbevinding van 27 augustus 2026. Ze vielen
   * eerst samen, en daardoor stond het geautomatiseerde systeem bij oplopend sorteren op het
   * gemiddelde BOVENAAN — met een streepje in de positiekolom, maar wel boven positie 1. Dat is de
   * regel die volgens epic 31 §2 het teken is dat er iets stuk is, en hij las daar als de snelste.
   *
   * - `buitenSortering` — gedempt weergeven mét de uitleg erbij. Alleen `person-removed`.
   * - `onderaanInTabel` — onderaan blijven staan. Béide niet-collega's.
   * - `teltMeeInRangschikking` — een positie krijgen. Geen van beide.
   */
  onderaanInTabel: boolean;
  /**
   * Of het filter op deze regel gezet mag worden.
   *
   * `person-removed` mag dat NIET: die grafiek zou over een onbekend aantal mensen gaan en zich
   * voordoen als de grafiek van één persoon (AC-9).
   */
  aanklikbaar: boolean;
}

/**
 * Optellen zonder van "niets" een nul te maken.
 *
 * `null` plus `null` blijft `null`, en dat is het hele punt: een stap waarin niemand een minuut
 * droeg heeft geen meting, en dat is iets anders dan een meting die op nul uitkwam.
 */
function telOp(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return a + b;
}

/** Minuten gedeeld door behandelingen, en niets zodra er niets te delen valt. */
function gemiddelde(minuten: number | null, behandelingen: number | null): number | null {
  if (minuten === null || behandelingen === null || behandelingen <= 0) return null;
  return minuten / behandelingen;
}

/**
 * Eén stap van één persoon, of niets als die stap in deze periode niet voorkomt.
 *
 * EEN REGEL ZONDER MINUUT WORDT IN ZIJN GEHEEL OVERGESLAGEN — ook zijn stuks. Dat is een bevinding
 * uit de tweede reviewronde van 30 augustus 2026: hier telden de `tradeItems` van zo'n regel wél
 * mee, terwijl `stapUit` in `reeks.ts` de regel volledig oversloeg. Op de regels
 * `{ minutes: null, tradeItems: 3 }` en `{ minutes: 60, tradeItems: 5 }` gaf de tabel dus 8
 * behandelingen en 7,5 minuut per stuk waar de grafiek 5 en 12,0 tekende — en sinds story 33.1
 * staan die twee op dezelfde gekozen stap naast elkaar op het scherm.
 *
 * Overslaan is de kant die het antwoordcontract voorschrijft, om twee redenen. Zo'n regel hoort er
 * helemaal niet te zijn: "Een stap waarin diegene geen minuut droeg krijgt GEEN regel, ook niet één
 * met nul: leeg is geen nul" (`HandlingTimePerPersonHandling`). En `tradeItems` telt uitsluitend de
 * stukken waaraan werkelijk gemeten is: "Een stuk waarop alleen een scherm openging telt niet mee:
 * het droeg geen minuut, en minuten gedeeld door stukken — de meest voor de hand liggende
 * afgeleide — zou er anders naast liggen" (`docs/api-contracts.md`). Een noemer zonder teller is
 * precies het getal dat daar verboden wordt.
 */
function stapVan(rij: HandlingTimePerPersonRow, stap: string): StapRegel {
  let minuten: number | null = null;
  let behandelingen: number | null = null;

  // Een antwoord van vóór story 31.1 draagt de uitsplitsing niet. Dan is er per stap niets te
  // tonen, en dat is iets anders dan nul — de regel houdt hier gewoon streepjes.
  for (const eigen of rij.handlings ?? []) {
    if (eigen.handling !== stap || eigen.minutes === null) continue;
    minuten = telOp(minuten, eigen.minutes);
    behandelingen = telOp(behandelingen, eigen.tradeItems);
  }

  return { minuten, behandelingen, gemiddeldePerStuk: gemiddelde(minuten, behandelingen) };
}

/** De regels van de tabel, in de volgorde waarin de bron ze gaf. Sorteren gebeurt apart. */
export function bouwTabelRegels(rijen: HandlingTimePerPersonRow[]): TabelRegel[] {
  return rijen.map((rij) => {
    const vastleggen = stapVan(rij, STAP_VASTLEGGEN);
    const valideren = stapVan(rij, STAP_VALIDEREN);
    const anoniem = rij.userNameOutcome === UITKOMST_PERSOON_VERWIJDERD;
    // GEEN VAN BEIDE IS EEN COLLEGA, en dus is geen van beide te filteren. `person-removed` is één
    // regel voor een ONBEKEND aantal mensen; filteren zou hen als één persoon laten lezen.
    // `automated-system` is de dienst zelf — die kan bij deze codes helemaal niet ontstaan, dus dat
    // hij er staat is zélf het signaal (epic 31 §2). Een grafiek van "de dienst" beantwoordt geen
    // vraag die iemand heeft (reviewbevinding 27-08-2026).
    const geenPersoon = anoniem || rij.userNameOutcome === UITKOMST_GEAUTOMATISEERD;

    return {
      userId: rij.userId,
      ...(rij.userName !== undefined ? { userName: rij.userName } : {}),
      userNameOutcome: rij.userNameOutcome,
      vastleggen,
      valideren,
      totaalMinuten: telOp(vastleggen.minuten, valideren.minuten),
      behandelingen: telOp(vastleggen.behandelingen, valideren.behandelingen),
      buitenSortering: anoniem,
      onderaanInTabel: geenPersoon,
      aanklikbaar: !geenPersoon,
    };
  });
}

/**
 * Story 31.6 — de positie.
 *
 * Wie meedoet aan de rangschikking. GEEN VAN BEIDE UITZONDERINGEN IS EEN COLLEGA (epic 31 §2):
 * `person-removed` vouwt een onbekend aantal mensen samen tot één regel, en `automated-system` is
 * de dienst zelf. Een positie zetten naast iets dat geen mens is, is een vergelijking die niemand
 * gemaakt heeft — en de regel voor verwijderde mensen zou met het opgetelde werk van tien mensen
 * ook nog eens boven aan de lijst kunnen belanden (AC-6).
 *
 * Let op het verschil met `buitenSortering`: dát gaat over de plek in de TABEL en geldt alleen voor
 * `person-removed`. Dit gaat over de NUMMERING, en daar valt het geautomatiseerde systeem ook buiten.
 */
export function teltMeeInRangschikking(regel: TabelRegel): boolean {
  return regel.userNameOutcome !== UITKOMST_PERSOON_VERWIJDERD
    && regel.userNameOutcome !== UITKOMST_GEAUTOMATISEERD;
}

/**
 * Het gemiddelde zoals het op het scherm STAAT: op één decimaal.
 *
 * Hier wordt op gerangschikt, en niet op de volle precisie. Twee mensen die allebei als 7,1 in de
 * tabel staan mogen geen verschillende positie krijgen om een verschil dat niemand kan zien (AC-5).
 */
const OP_EEN_DECIMAAL = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  useGrouping: false,
});

export function zoalsGetoond(waarde: number): number {
  // DEZELFDE AFRONDING ALS DE CEL, en niet `Math.round(waarde * 10) / 10`. Dat laatste stond hier
  // en het was fout (reviewbevinding 27-08-2026): vermenigvuldigen met tien duwt een waarde net
  // over de .5-grens die de opmaakfunctie van de cel juist naar beneden afrondt.
  //
  // GEMETEN: 11,1 minuten over 6 behandelingen is 1.8499999999999999. De cel toont "1,8", de oude
  // berekening maakte er 1,9 van, en naast 11,0 over 6 — die óók "1,8" toont — leverde dat de
  // posities 1 en 2 op voor twee regels die op het scherm hetzelfde getal dragen. Over 240.000
  // combinaties liepen er 201 uiteen; met deze vorm nul.
  //
  // `en-US` en niet de taal van de lezer: dit is een REKENSLEUTEL en geen tekst voor op het scherm.
  // Een taal met een komma als decimaalteken zou hier `Number('1,8')` opleveren, en dat is NaN.
  return Number(OP_EEN_DECIMAAL.format(waarde));
}

/**
 * De positie per persoon, gerangschikt op het GEMIDDELDE PER STUK van de gekozen stap.
 *
 * Nadrukkelijk niet op minuten: wie veel uren maakt is niet langzaam, en wie weinig uren maakt is
 * niet snel (AC-2).
 *
 * Wie in de gekozen stap geen gemiddelde heeft komt in deze kaart NIET voor — geen positie, en dus
 * ook niet de laatste plaats. Een streepje is iets anders dan achteraan staan (AC-4).
 *
 * Ex aequo deelt de plek, en de volgende slaat de gedeelde plekken over: twee keer 7,1 geeft twee
 * keer positie 1 en de volgende krijgt 3 (AC-5).
 */
export function bepaalPosities(regels: TabelRegel[], stap: StapKeuze): Map<number, number> {
  const mee = regels
    .filter(teltMeeInRangschikking)
    .flatMap((regel) => {
      const gemiddeldePerStuk = regel[stap].gemiddeldePerStuk;
      return gemiddeldePerStuk === null ? [] : [{ userId: regel.userId, getoond: zoalsGetoond(gemiddeldePerStuk) }];
    })
    .sort((a, b) => a.getoond - b.getoond);

  const posities = new Map<number, number>();
  let vorigeWaarde: number | null = null;
  let vorigePositie = 0;

  mee.forEach((deelnemer, index) => {
    if (vorigeWaarde !== null && deelnemer.getoond === vorigeWaarde) {
      posities.set(deelnemer.userId, vorigePositie);
      return;
    }
    vorigeWaarde = deelnemer.getoond;
    vorigePositie = index + 1;
    posities.set(deelnemer.userId, vorigePositie);
  });

  return posities;
}

/** De vertaalfunctie zoals `react-i18next` hem geeft, tot precies wat de kolommen gebruiken. */
type Vertaal = (sleutel: string, opties?: Record<string, unknown>) => string;

/**
 * Wat de kolommen nodig hebben dat niet in de regel staat (spec-tabelfilters §2): de vertaler voor
 * de naam, de stand van het stapfilter (welke kolommen er zijn) en de posities (die hangen aan de
 * gekozen stap en worden over ALLE regels bepaald, niet over de gefilterde).
 */
export interface BehandeltijdContext extends Context {
  t: Vertaal;
  stapFilter: StapFilter;
  posities: ReadonlyMap<number, number>;
}

const K = 'handlingTime.table';

/** Een minutenkolom: getal, eerste klik aflopend (de meeste minuten bovenaan). */
function getalKolom(
  sleutel: string,
  kopSleutel: string,
  waarde: (regel: TabelRegel) => number | null,
  zichtbaar?: (ctx: BehandeltijdContext) => boolean,
): Kolom<TabelRegel, BehandeltijdContext> {
  return { sleutel, kopSleutel: `${K}.${kopSleutel}`, type: 'getal', eersteRichting: 'af', waarde, ...(zichtbaar ? { zichtbaar } : {}) };
}

const zonderValideren = (ctx: BehandeltijdContext) => ctx.stapFilter !== 'valideren';
const zonderVastleggen = (ctx: BehandeltijdContext) => ctx.stapFilter !== 'vastleggen';

/**
 * spec-tabelfilters story 6 — de kolommen van de behandeltijdtabel, op één plek.
 *
 * Sorteren gebeurt in `@xxtract/tabelfilters`; de drie regels die niet mogen sneuvelen blijven:
 *
 * 1. Wie in de gesorteerde stap niets droeg zakt naar ONDEREN, in béide richtingen (AC-4): de
 *    package zet `null` per kolom altijd onderaan.
 * 2. De twee niet-collega's blijven achteraan, buiten de sortering (AC-5): `vastOnderaan`.
 * 3. Op de positiekolom zakt iedereen zonder positie naar onderen (story 31.6, AC-7): de posities
 *    komen via de context mee, want ze hangen aan de gekozen stap en niet aan de regel.
 *
 * Op één gekozen stap zijn de kolommen van de andere stap en het totaal verborgen (AC-3); een
 * bewaarde sortering op zo'n kolom wordt overgeslagen maar blijft in de stand, zodat "Beide" hem
 * weer oppakt. De persoonkolom heet in een link `naam`: `persoon` is de persoon van de grafiek.
 */
export const BEHANDELTIJD_LIJST: Lijst<TabelRegel, BehandeltijdContext> = {
  kolommen: [
    // Oplopend als eerste klik, want positie 1 hoort bovenaan te komen.
    { sleutel: 'positie', kopSleutel: `${K}.positie`, type: 'getal', waarde: (regel, ctx) => ctx.posities.get(regel.userId) ?? null },
    { sleutel: 'persoon', kopSleutel: `${K}.person`, type: 'tekst', urlNaam: 'naam', waarde: (regel, ctx) => naamVoorRegel(regel, ctx.t) },
    getalKolom('vastleggen', 'vastleggen', (regel) => regel.vastleggen.minuten, zonderValideren),
    getalKolom('valideren', 'valideren', (regel) => regel.valideren.minuten, zonderVastleggen),
    getalKolom('totaal', 'totaal', (regel) => regel.totaalMinuten, (ctx) => ctx.stapFilter === 'beide'),
    getalKolom('behandelingen', 'behandelingen', (regel) => regel.behandelingen),
    getalKolom('gemiddeldeVastleggen', 'gemiddeldeVastleggen', (regel) => regel.vastleggen.gemiddeldePerStuk, zonderValideren),
    getalKolom('gemiddeldeValideren', 'gemiddeldeValideren', (regel) => regel.valideren.gemiddeldePerStuk, zonderVastleggen),
  ],
  // De meeste minuten bovenaan: het totaal, of op één gekozen stap die stap.
  standaardSortering: (ctx) => [{ sleutel: ctx.stapFilter === 'beide' ? 'totaal' : ctx.stapFilter, richting: 'af' }],
  vastOnderaan: (regel) => regel.onderaanInTabel,
};

/**
 * Story 33.2 — of de volledige tabel open of dicht staat, uit het webadres.
 *
 * Sinds deze story staat de tabel ÓNDER de aandachtslijst en is hij ingeklapt bij het laden: het
 * blok erboven zegt waar de tijd boven de norm zit, en wie daarna alle cijfers wil ziet ze in één
 * klik. Wat epic 31 opleverde vervalt daarmee niet — de positiekolom, de eerlijkheidsnoot en de
 * sorteerbare kolommen staan er onveranderd (AC-16).
 *
 * Open of dicht staat in het ADRES en niet in het geheugen van het scherm, om dezelfde reden als de
 * gekozen stap en de gekozen persoon: een gedeelde link hoort te openen zoals de afzender hem zag
 * (AC-17). Afwezig is dicht, en dat is ook de beginstand — een link zonder deze parameter opent dus
 * zoals het scherm bedoeld is.
 */
export const TABEL_PARAMETER = 'tabel';

/** De enige waarde die de tabel opent. Alles anders — ook een typefout — laat hem gewoon dicht. */
export const TABEL_OPEN = 'open';

export function leesTabelOpen(waarde: string | null | undefined): boolean {
  return waarde === TABEL_OPEN;
}
